import {
  CheckCircle2,
  CircleSlash,
  MonitorSmartphone,
  PlayCircle,
  RadioTower,
  ScrollText,
  TriangleAlert,
  WifiOff,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { Button } from '@krp/ui-kit'

import type { RegisterKind, RegisterRowVm } from '../../model/types'

const KIND_ICONS: Readonly<Record<RegisterKind, ReactNode>> = {
  free: <CheckCircle2 size={18} aria-hidden="true" />,
  continue: <PlayCircle size={18} aria-hidden="true" />,
  occupied: <CircleSlash size={18} aria-hidden="true" />,
  unreachable: <WifiOff size={18} aria-hidden="true" />,
  unavailable: <TriangleAlert size={18} aria-hidden="true" />,
}

/**
 * Касса магазина источника в блоке «Касса» (RP-FB22 3): выбирается, а не запускается — «Открыть смену» одна, внизу.
 * Свободная и «смена открыта, никто не работает» (её можно продолжить) выбираются; занятая недоступна с причиной
 * (кто и с какого времени), не отвечающая предлагает «Проверить связь». Состояние — значок, слова и рамка, не только цвет;
 * выбранная — ещё и словом «Выбрана». «Освободить…» и «Журнал кассы» — только при праве.
 */
export function RegisterCard({
  row,
  pick,
  selected,
  workingText,
  busy,
  onCheck,
  onRelease,
  onJournal,
}: {
  row: RegisterRowVm
  /**
   * Переключатель выбора кассы с её названием (подпись-переключатель). Его собирает блок «Касса» внутри группы с ⓘ;
   * `null` — кассу выбрать нельзя, показывается только название.
   */
  pick: ReactNode
  selected: boolean
  /** Что сейчас делается с этой кассой (проверка связи): слово в строке состояния. */
  workingText: string | null
  busy: boolean
  onCheck: () => void
  onRelease: () => void
  onJournal: () => void
}) {
  // «Проверить связь» отдельной кнопкой — у кассы, чья главная кнопка другая (у «не отвечает» она и есть главная).
  const secondaryCheck = row.primary !== 'check' && row.kind !== 'occupied' && row.kind !== 'unavailable'
  return (
    <li className="aqsi-card rmk-register" data-state={row.kind} data-selected={selected || undefined} aria-label={`Касса ${row.name}`}>
      {pick ?? (
        <p className="aqsi-card__title">
          <MonitorSmartphone size={16} aria-hidden="true" />
          {row.name}
        </p>
      )}
      <p className="aqsi-card__headline">
        {KIND_ICONS[row.kind]}
        {row.headline}
      </p>
      {row.detail !== null && <p className="aqsi-card__text">{row.detail}</p>}
      {workingText !== null && (
        <p className="aqsi-card__text" role="status">
          {workingText}
        </p>
      )}
      <div className="aqsi-card__actions rmk-register__actions">
        {row.primary === 'check' && (
          <Button
            variant="primary"
            icon={<RadioTower size={16} aria-hidden="true" />}
            actionId="aqsi.rmk.check-device"
            disabled={!row.primaryEnabled || busy}
            onClick={onCheck}
          >
            Проверить связь
          </Button>
        )}
        {secondaryCheck && (
          <Button variant="ghost" icon={<RadioTower size={14} aria-hidden="true" />} actionId="aqsi.rmk.check-device" disabled={busy} onClick={onCheck}>
            Проверить связь
          </Button>
        )}
        {row.canRelease && (
          <Button variant="ghost" actionId="aqsi.rmk.release" onClick={onRelease}>
            Освободить…
          </Button>
        )}
        {row.canJournal && (
          <Button variant="ghost" icon={<ScrollText size={14} aria-hidden="true" />} actionId="aqsi.rmk.journal" onClick={onJournal}>
            Журнал кассы
          </Button>
        )}
      </div>
    </li>
  )
}
